/**
 * SentDocumentFilters Component
 *
 * Filter controls for the sent documents list including:
 * - Patient picker
 * - Document type multi-select (Form / Document / Lab)
 * - Channel multi-select (SMS / Email)
 * - Delivery status multi-select
 * - Date range picker
 * - Clear filters button
 */

import React, { useCallback } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector/DateRangeSelector';
import PatientPicker from '@components/Patient/PatientPicker';
import { ChipMultiSelect } from '@components/ui/forms/ChipMultiSelect';
import { useEventTracking } from '@services/statsig';
import {
  Assignment as FormIcon,
  Clear as ClearIcon,
  Description as DocumentIcon,
  FilterList as FilterListIcon,
  Science as LabIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Stack } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';

import type { Patient } from '../../../types/types';
import type { SentDocumentFiltersState } from '../hooks/useSentDocumentsList';

const CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
] as const;

const DELIVERY_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
] as const;

const SENT_DOCUMENT_TYPE_OPTIONS = [
  { value: 'form', label: 'Form' },
  { value: 'document', label: 'Document' },
  { value: 'lab', label: 'Lab' },
] as const;

const TYPE_FILTER_ICONS: Record<string, React.ElementType> = {
  form: FormIcon,
  document: DocumentIcon,
  lab: LabIcon,
};

interface SentDocumentFiltersProps {
  filters: SentDocumentFiltersState;
  onPatientIdChange: (patientId: string | null) => void;
  onChannelsChange: (channels: string[]) => void;
  onStatusesChange: (statuses: string[]) => void;
  onCommunicationTypesChange: (types: string[]) => void;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  isLoading?: boolean;
}

export const SentDocumentFilters: React.FC<SentDocumentFiltersProps> = ({
  filters,
  onPatientIdChange,
  onChannelsChange,
  onStatusesChange,
  onCommunicationTypesChange,
  onStartDateChange,
  onEndDateChange,
  onClearFilters,
  hasActiveFilters,
  isLoading = false,
}) => {
  const { document: documentMetrics } = useEventTracking();

  const activeFilterCount = [
    filters.patientId,
    filters.communicationTypes.length > 0,
    filters.channels.length > 0,
    filters.statuses.length > 0,
    filters.startDate || filters.endDate,
  ].filter(Boolean).length;

  const trackFilter = useCallback(
    (filter_type: 'patient' | 'channel' | 'status' | 'communication_type' | 'date_range' | 'clear', filter_value?: string) => {
      documentMetrics.sentDocumentsFilterChanged({
        filter_type,
        filter_value,
        active_filter_count: activeFilterCount,
      });
    },
    [documentMetrics, activeFilterCount],
  );

  const handlePatientSelect = useCallback(
    (patient: Patient | undefined) => {
      onPatientIdChange(patient?.patient_id || null);
      trackFilter('patient', patient?.patient_id ?? undefined);
    },
    [onPatientIdChange, trackFilter],
  );

  const handleChannelsChange = useCallback(
    (channels: string[]) => {
      onChannelsChange(channels);
      trackFilter('channel', channels.join(',') || undefined);
    },
    [onChannelsChange, trackFilter],
  );

  const handleStatusesChange = useCallback(
    (statuses: string[]) => {
      onStatusesChange(statuses);
      trackFilter('status', statuses.join(',') || undefined);
    },
    [onStatusesChange, trackFilter],
  );

  const handleCommunicationTypesChange = useCallback(
    (types: string[]) => {
      onCommunicationTypesChange(types);
      trackFilter('communication_type', types.join(',') || undefined);
    },
    [onCommunicationTypesChange, trackFilter],
  );

  const handleDateRangeChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      onStartDateChange(range.start ? dayjs(range.start) : null);
      onEndDateChange(range.end ? dayjs(range.end) : null);
      trackFilter('date_range');
    },
    [onStartDateChange, onEndDateChange, trackFilter],
  );

  const handleClearFilters = useCallback(() => {
    onClearFilters();
    trackFilter('clear');
  }, [onClearFilters, trackFilter]);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        flexWrap="wrap"
      >
        {/* Patient Picker */}
        <Box sx={{ minWidth: 250, flex: { xs: 1, md: 'none' } }}>
          <PatientPicker onPatientSelect={handlePatientSelect} size="small" disableAddNew />
        </Box>

        {/* Document Type */}
        <ChipMultiSelect
          labelId="comm-type-label"
          label="Type"
          value={filters.communicationTypes}
          options={SENT_DOCUMENT_TYPE_OPTIONS}
          onChange={handleCommunicationTypesChange}
          onOnly={v => handleCommunicationTypesChange([v])}
          onClear={() => handleCommunicationTypesChange([])}
          disabled={isLoading}
          icons={TYPE_FILTER_ICONS}
        />

        {/* Channel */}
        <ChipMultiSelect
          labelId="channel-label"
          label="Channel"
          value={filters.channels}
          options={CHANNEL_OPTIONS}
          onChange={handleChannelsChange}
          onOnly={v => handleChannelsChange([v])}
          onClear={() => handleChannelsChange([])}
          disabled={isLoading}
          minWidth={120}
        />

        {/* Delivery Status */}
        <ChipMultiSelect
          labelId="delivery-status-label"
          label="Status"
          value={filters.statuses}
          options={DELIVERY_STATUS_OPTIONS}
          onChange={handleStatusesChange}
          onOnly={v => handleStatusesChange([v])}
          onClear={() => handleStatusesChange([])}
          disabled={isLoading}
        />

        {/* Date Range */}
        <Box sx={{ minWidth: 200 }}>
          <DateRangeSelector
            value={{
              start: filters.startDate?.toDate() ?? null,
              end: filters.endDate?.toDate() ?? null,
            }}
            onChange={handleDateRangeChange}
            label="Date Range"
            size="small"
            disabled={isLoading}
          />
        </Box>

        {/* Filter Status & Clear */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { md: 'auto' } }}>
          {activeFilterCount > 0 && (
            <Chip
              icon={<FilterListIcon fontSize="small" />}
              label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              disabled={isLoading}
            >
              Clear All
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default SentDocumentFilters;
